import { format, formatDistanceToNow } from 'date-fns';
import {
  af, ar, az, da, de, el, enUS, es, faIR, fr, frCA, he, hi, hu, id, it, ja,
  ka, kk, ko, lv, nl, pl, pt, ptBR, ro, ru, sq, sv, te, th, tr, uk, uz, vi,
  zhCN, zhHK,
} from 'date-fns/locale';

export const TICKET_TYPES = ['Issue', 'Bug', 'Feature', 'Other'];

export const ACTIVE_STATUSES = ['Open', 'Escalated'];

export const isActiveStatus = (status) => ACTIVE_STATUSES.includes(status);

// Maps openedx locale codes (lowercased, hyphenated) to date-fns locale objects.
// Codes not listed here fall back to the base-language entry, then to English.
const DATE_FNS_LOCALES = {
  af,
  ar,
  az,
  da,
  de,
  el,
  en: enUS,
  es,
  'es-419': es,
  'es-es': es,
  fa: faIR,
  fr,
  'fr-ca': frCA,
  he,
  hi,
  hu,
  id,
  it,
  ja,
  ka,
  kk,
  ko,
  lv,
  nl,
  pl,
  pt,
  'pt-br': ptBR,
  'pt-pt': pt,
  ro,
  ru,
  sq,
  sv,
  te,
  th,
  tr,
  uk,
  uz,
  vi,
  'zh-cn': zhCN,
  'zh-hk': zhHK,
};

export const getDateFnsLocale = (code) => {
  const norm = (code || '').toLowerCase();
  return DATE_FNS_LOCALES[norm] || DATE_FNS_LOCALES[norm.split('-')[0]] || enUS;
};

export const getStatusBadgeColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'closed':
      return 'success';
    default:
      return 'danger';
  }
};

export const getTypeIconColor = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'bug':
      return 'var(--pgn-color-red-700)';
    case 'issue':
      return 'var(--pgn-color-yellow-700)';
    case 'feature':
      return 'var(--pgn-color-green-700)';
    default:
      return 'var(--pgn-color-gray-700)';
  }
};

const TYPE_MESSAGE_KEYS = {
  issue: 'issueType',
  bug: 'bugType',
  feature: 'featureType',
  other: 'otherType',
};

export const getStatusLabel = (status, intl, messages) => {
  if (!status) { return ''; }
  if ((status || '').toLowerCase() === 'closed') {
    return intl.formatMessage(messages.statusClosed);
  }
  return intl.formatMessage(messages.statusActive);
};

export const getTypeLabel = (type, intl, messages) => {
  if (!type) { return ''; }
  const key = TYPE_MESSAGE_KEYS[(type || '').toLowerCase()];
  return key ? intl.formatMessage(messages[key]) : type;
};

export const formatTicketDate = (iso, locale) => {
  if (!iso) { return ''; }
  try {
    return format(new Date(iso), 'dd MMM, yyyy', { locale: getDateFnsLocale(locale) });
  } catch {
    return '';
  }
};

export const formatTicketTime = (iso, locale) => {
  if (!iso) { return ''; }
  try {
    return format(new Date(iso), 'h:mm a', { locale: getDateFnsLocale(locale) });
  } catch {
    return '';
  }
};

export const formatTimeAgo = (iso, locale) => {
  if (!iso) { return ''; }
  try {
    return formatDistanceToNow(new Date(iso), {
      addSuffix: true,
      locale: getDateFnsLocale(locale),
    });
  } catch {
    return '';
  }
};

export const formatTicketShortId = (ticketId) => {
  if (!ticketId) { return ''; }
  return `#${String(ticketId).split('-')[0]}`;
};
